import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Plus } from 'lucide-react';
import { PageHead } from '@/admin-area/ui/PageHead';
import { PhonePrimary } from '@/admin-area/ui/PhonePrimary';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { ConfigPages, ConfigProblem, ConfigurationGate } from './ConfigurationParts';
import { listPurposes, type PurposePage } from './api';
import { PurposePanel } from './PurposePanel';
import { PurposeToolbar } from './PurposeFilters';
import { PurposeTable, PurposeCards } from './PurposeLists';
import { usePurposeQuery } from './use-purpose-query';
type View = ReturnType<typeof usePurposeQuery>;
export function PurposesPage() {
  return (
    <ConfigurationGate>
      <Purposes />
    </ConfigurationGate>
  );
}
function usePurposeList(view: View) {
  const [data, setData] = useState<PurposePage | null>(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const key = JSON.stringify(view.query);
  useEffect(() => {
    let live = true;
    setData(null);
    setError('');
    void listPurposes(JSON.parse(key) as typeof view.query).then((outcome) => {
      if (!live) return;
      if (outcome.ok) setData(outcome.value);
      else setError(outcome.code);
    });
    return () => {
      live = false;
    };
  }, [key, retry]);
  return { data, error, reload: () => setRetry((n) => n + 1) };
}
function Purposes() {
  const { t } = useTranslation('expenses');
  const navigate = useNavigate();
  const view = usePurposeQuery();
  const loaded = usePurposeList(view);
  const selected = view.params.get('purpose');
  // One «create» on a phone, as the lists have it: the head's button up from 620 px, the
  // floating one below (L8).
  const create = (
    <Button
      className="min-h-11"
      data-purpose-return
      data-purpose-create
      onClick={() => void navigate(view.target('new'))}
    >
      <Plus />
      {t('configuration.new')}
    </Button>
  );
  return (
    <section className="flex min-w-0 flex-col gap-5" aria-label={t('configuration.title')}>
      <PageHead
        title={t('configuration.title')}
        summary={t('configuration.summary')}
        actions={<span className="page-primary">{create}</span>}
      />
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
        <Link
          className="text-foreground font-semibold hover:underline inline-flex min-h-11 items-center"
          to="/expenses/settings/note-types"
        >
          {t('configuration.noteTypes')}
          <span aria-hidden="true" className="ml-1">
            →
          </span>
        </Link>
        <Link
          className="text-foreground font-semibold hover:underline inline-flex min-h-11 items-center"
          to="/expenses/settings/reserved-purposes"
        >
          {t('configuration.reservedLink')}
          <span aria-hidden="true" className="ml-1">
            →
          </span>
        </Link>
      </div>
      <PurposeToolbar view={view} />
      <PurposeResults view={view} {...loaded} />
      <PhonePrimary>{create}</PhonePrimary>
      {selected ? (
        <PurposePanel key={selected} id={selected} onClose={view.close} onSaved={loaded.reload} />
      ) : null}
    </section>
  );
}
function PurposeResults({
  view,
  data,
  error,
  reload,
}: {
  readonly view: View;
  readonly data: PurposePage | null;
  readonly error: string;
  readonly reload: () => void;
}) {
  const { t } = useTranslation('expenses');
  const navigate = useNavigate();
  if (error) return <ConfigProblem code={error} retry={reload} />;
  if (data === null)
    return (
      <SkeletonRegion label={t('configuration.loading')}>
        <Skeleton className="h-40 w-full" />
      </SkeletonRegion>
    );
  const narrowed =
    !!view.query.search ||
    !!view.query.noteType ||
    view.params.has('active') ||
    view.params.has('restricted') ||
    view.view === 'archived' ||
    view.view === 'reserved';
  return (
    <>
      {data.items.length === 0 ? (
        <div className="bg-muted space-y-2 rounded-xl p-6">
          <h2 className="font-semibold">
            {t(narrowed ? 'configuration.noMatches' : 'configuration.none')}
          </h2>
          <Button
            variant="outline"
            className="min-h-11"
            onClick={() => (narrowed ? view.clear() : void navigate(view.target('new')))}
          >
            {t(narrowed ? 'configuration.reset' : 'configuration.new')}
          </Button>
        </div>
      ) : (
        <>
          <PurposeTable items={data.items} view={view} />
          <PurposeCards items={data.items} view={view} />
        </>
      )}
      <div className="text-muted-foreground flex flex-wrap items-center justify-between gap-3 text-sm">
        <p role="status">{t('configuration.total', { count: data.total })}</p>
        <ConfigPages
          page={data.page}
          pageSize={data.pageSize}
          total={data.total}
          onPage={(n) => view.change('page', String(n))}
        />
      </div>
    </>
  );
}
